import React, { useState } from 'react';
import { verifyShopCredentials } from '../../lib/supabase/api/update-kyc-info';
import useKYCStore from '../../store/kycStore';
import { FiMail, FiLock, FiLogIn } from 'react-icons/fi';
import { toast } from 'react-hot-toast';
import IconWrapper from '../print-steps/IconWrapper';
import { successToastStyle, errorToastStyle } from '../../utils/toastConfig';

const AuthenticationStep: React.FC = () => {
  const { setIsAuthenticated, setShopAccountId, nextStep, setIsLoading, isLoading } = useKYCStore(); 
  const [email, setEmail] = useState('');
  const [secret, setSecret] = useState('');
  const [errors, setErrors] = useState({ email: '', secret: '' });

  const validateForm = () => {
    const newErrors = { email: '', secret: '' };
    let isValid = true;

    if (!email) {
      newErrors.email = 'Email is required';
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Email is invalid';
      isValid = false;
    }

    if (!secret) {
      newErrors.secret = 'Secret key is required';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    
    try {
      // Verify shop credentials
      const shopAccountId = await verifyShopCredentials(email, secret); 
      
      if (shopAccountId) {
        setShopAccountId(shopAccountId);
        setIsAuthenticated(true);
        nextStep();
        toast.success('Authentication successful!', successToastStyle);
      } else {
        toast.error('Invalid email or secret key. Please try again.', errorToastStyle);
      }
    } catch (error) {
      console.error('Authentication error:', error);
      toast.error('Authentication failed. Please try again later.', errorToastStyle);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="w-full max-w-md mx-auto px-2">
      <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-4 sm:mb-6 text-center">Shop Authentication</h2>
      
      <div className="bg-blue-50 p-3 sm:p-4 rounded-lg mb-4 sm:mb-6">
        <p className="text-xs sm:text-sm text-blue-700">
          Enter the email and secret key provided during shop registration to continue with KYC verification.
        </p>
      </div>
      
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
            Email Address
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <IconWrapper icon={FiMail} className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="email"
              id="email"
              className={`appearance-none block w-full pl-10 pr-3 py-2 border ${
                errors.email ? 'border-red-300' : 'border-gray-300'
              } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
              placeholder="Enter email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
        </div>

        <div className="mb-6">
          <label htmlFor="secret" className="block text-sm font-medium text-gray-700 mb-1">
            Secret Key
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <IconWrapper icon={FiLock} className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="password"
              id="secret"
              className={`appearance-none block w-full pl-10 pr-3 py-2 border ${
                errors.secret ? 'border-red-300' : 'border-gray-300'
              } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500`}
              placeholder="Enter secret key"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
            />
          </div>
          {errors.secret && <p className="mt-1 text-sm text-red-600">{errors.secret}</p>}
        </div>

        <button
          type="submit"
          disabled={isLoading}
          className="w-full flex justify-center items-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
        >
          {isLoading ? (
            <>
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Verifying...
            </>
          ) : (
            <>
              <IconWrapper icon={FiLogIn} className="mr-2" />
              Continue to KYC
            </>
          )}
        </button>
      </form>

      <div className="mt-4 sm:mt-6 text-center">
        <p className="text-xs sm:text-sm text-gray-600">
          Don't have an account yet?{' '}
          <a href="/shop-registration" className="font-medium text-blue-600 hover:text-blue-500">
            Register your shop here
          </a>
        </p>
      </div>
    </div>
  );
};

export default AuthenticationStep;
